import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnvironment } from '../../hooks/useEnvironment';

interface Feature {
  id: number;
  title: string;
  description: string;
  image: string;
}

interface Testimonial {
  id: number;
  bgColor: string;
  rating: number;
  review: string;
  name: string;
  pet: string;
  image: string;
}

interface Pet {
  id: number;
  name: string;
  image: string;
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  // Self-serve signup is dev/local-only — see backend
  // app.core.version_info.is_self_serve_signup_allowed. The hook fetches
  // GET /api/version once at module scope and gates every CTA below.
  const { selfServeSignup } = useEnvironment();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const features: Feature[] = [
    { id: 1, title: "Easy Registration", description: "Register your pets in just a few clicks once your property manager invites you.", image: '/landing/ThumbsUp.svg' },
    { id: 2, title: "Keep Pets Healthy", description: "Our portal helps keep pets healthy by reminding you when vaccinations are due.", image: '/landing/Heart.svg' },
    { id: 3, title: "Your Data Stays safe", description: "Your privacy is our priority. Secured, protected, and never shared.", image: '/landing/Shield.svg' },
    { id: 4, title: "A Safer Home", description: "A safer home starts with knowing the pets in your community.", image: '/landing/House.svg' }
  ];

  const testimonials: Testimonial[] = [
    { id: 1, bgColor: 'bg-[#F0FAFF]', rating: 5, review: '"GetPawtected made enrolling my dog so easy! The process was quick and the coverage is comprehensive!"', name: 'Sarah Johnson', pet: 'Pet parent to a Golden Retriever', image: '/landing/SarahJohnson.jpg' },
    { id: 2, bgColor: 'bg-[#F0FAFF]', rating: 5, review: '"The 24/7 vet support has been a lifesaver. I can always get help when I need it!"', name: 'Mike Chen', pet: 'Pet parent to a Persian Cat', image: '/landing/MikeChen.jpg' },
    { id: 3, bgColor: 'bg-[#F0FAFF]', rating: 5, review: '"Fast claims processing and excellent customer service! Highly recommend."', name: 'Emily Rodriguez', pet: 'Pet parent to a French Bulldog', image: '/landing/EmilyRodriguez.jpg' },
    { id: 4, bgColor: 'bg-[#F0FAFF]', rating: 5, review: '"GetPawtected made enrolling my dog so easy! The process was quick and the coverage is comprehensive!"', name: 'Tim Peterson', pet: 'Pet parent to a Yellow Lab', image: '/landing/TimPeterson.jpg' }
  ];

  const pets: Pet[] = [
    { id: 1, name: 'Bruce', image: '/landing/Bruce.png' },
    { id: 2, name: 'Gus', image: '/landing/Gus.png' },
    { id: 3, name: 'Blondie', image: '/landing/Blondie.png' },
    { id: 4, name: 'Poppy', image: '/landing/Poppy.png' },
    { id: 5, name: 'Luna', image: '/landing/Luna.png' },
    { id: 6, name: 'Grandma', image: '/landing/Grandma.jpg' },
    { id: 7, name: 'Hazel', image: '/landing/Hazel.png' }
  ];

  const StarIcon: React.FC = () => (
    <svg className="w-8 h-8 text-yellow-400 fill-current" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );

  return (
    <div className="w-full min-h-screen bg-white">
      <header className="fixed top-0 left-0 w-full bg-white shadow-sm z-50 h-20" role="banner">
        <div className="flex justify-between items-center px-4 md:px-8 lg:px-16 py-6">
          <button 
            className="cursor-pointer" 
            onClick={() => scrollToSection('main')}
            aria-label="GetPawtected home - scroll to top"
          >
            <img src="landing/getpawtected.svg" alt="GetPawtected logo" className="h-10" />
          </button>
          <div className="flex items-center space-x-4 md:space-x-8">
            {/* Self-serve sign-up: dev/local only; hard-gated on staging/prod. */}
            {selfServeSignup && (
              <nav className="hidden md:flex space-x-4 lg:space-x-8 font-inter" aria-label="Main navigation">
                <button
                  className="text-lg font-bold text-[color:var(--pmc-primary)] hover:text-purple-700 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2 rounded"
                  onClick={() => navigate('/signup')}
                  aria-label="Create a new profile"
                >
                  CREATE A PROFILE
                </button>
              </nav>
            )}
            <button
              className="font-poppins border-2 border-gray-400 px-4 md:px-6 py-2 rounded-full text-gray-600 font-bold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2"
              onClick={() => navigate('/login')}
              aria-label="Sign in to your account"
            >
              SIGN IN
            </button>
          </div>
        </div>
      </header>

      <main id="main-content">
        <section id="main" className="relative h-screen flex items-center mt-5" aria-labelledby="hero-heading">
          <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
            <img className="absolute inset-0 w-full h-full object-cover rounded-3xl" src="/landing/puppy.png" alt="Happy puppy in a home environment" role="img"/>
            <div className="relative z-10 max-w-2xl py-10 md:py-20">
              <h1 id="hero-heading" className="text-5xl md:text-7xl lg:text-8xl font-medium text-black mb-4 font-poppins">Have a Pet?</h1>
              <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 font-poppins">
                <span className="text-[color:var(--pmc-accent)]">Get</span>
                <span className="text-purple-900">Pawtected</span>
              </h2>
              <p className="text-xl md:text-2xl leading-7 md:leading-8 text-black mb-8 md:mb-12 max-w-xl font-inter">
                Get Covered helps build a community that embraces pets by supporting responsible ownership through advanced software.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 font-poppins">
                {/* Self-serve sign-up: dev/local only; hard-gated on staging/prod. */}
                {selfServeSignup && (
                  <button
                    className="bg-[color:var(--pmc-accent)] hover:bg-[#2196CC] text-white px-6 md:px-8 py-3 rounded-full font-bold uppercase focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2"
                    onClick={() => navigate('/signup')}
                    aria-label="Get started with pet registration"
                  >
                    GET STARTED
                  </button>
                )}
                <button
                  className="bg-white border-2 border-gray-400 text-gray-600 px-6 md:px-8 py-3 rounded-full font-bold uppercase hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2"
                  onClick={() => scrollToSection('features')}
                  aria-label="Learn more about our features"
                >
                  LEARN MORE
                </button>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="relative text-center font-inter text-black pt-6 md:pt-10 pb-12 md:pb-14" aria-labelledby="features-heading">
          <img className="absolute top-0 left-0 w-full h-full object-cover" src="/landing/Gradient.png" alt="" aria-hidden="true" />
          <div className="relative text-center mb-8 md:mb-16 px-4">
            <div className="mb-6">
              <h2 id="features-heading" className="font-poppins mb-4 leading-tight">
                <span className="text-black text-6xl font-medium font-['Poppins']">What is </span>
                <span className="text-sky-500 text-6xl font-bold font-['Poppins']">Get</span>
                <span className="text-purple-900 text-6xl font-bold font-['Poppins']">Pawtected</span>
                <span className="text-black text-6xl font-medium font-['Poppins']"> ?</span>
              </h2>
            </div>
            <p className="font-inter text-black max-w-4xl mx-auto text-center text-lg md:text-xl lg:text-2xl leading-7 md:leading-8">
            Trusted by residents, our software helps create a safer, more comfortable community by providing clear insights into every pet. </p>
          </div>
          <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto px-4">
            {features.map(feature => (
              <article key={feature.id} className="bg-white p-6 md:p-8 text-center rounded-3xl">
                <div className="flex justify-center mb-6" aria-hidden="true">
                  <img src={feature.image} alt="" className="w-12 md:w-16 h-12 md:h-16" />
                </div>
                <h3 className="text-black mb-4 font-inter text-center font-bold text-xl md:text-2xl lg:text-3xl leading-tight">{feature.title}</h3>
                <p className="text-black font-inter text-center text-base md:text-lg leading-6 md:leading-7">{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="relative text-center font-inter text-black py-12 md:py-16" aria-labelledby="pets-heading">
          <div className="relative z-10">
            <h2 id="pets-heading" className="text-3xl md:text-5xl lg:text-6xl font-medium font-poppins mb-6 px-4">Meet Our Happy Pet Families!</h2>
            <p className="text-lg md:text-xl lg:text-2xl leading-relaxed mb-8 md:mb-12 px-4">Just a few of the pets we are already protecting!</p>
            <div className="max-w-7xl mx-auto px-4">
              <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-8" role="list">
                {pets.map(pet => (
                  <li key={pet.id} className="flex flex-col items-center">
                    <img className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-full mb-4" src={pet.image} alt={`${pet.name}, a protected pet`} />
                    <span className="text-base md:text-lg lg:text-xl">{pet.name}</span>
                  </li>
                ))}
              </ul>
            </div>
            </div>
        </section>

        <section id="contact" className="relative py-10 md:py-20" aria-labelledby="faq-heading">
          {/* Gradient Background */}
          <img 
            className="absolute top-0 left-0 w-full h-full object-cover" 
            src="/landing/Gradient.png" 
            alt="" 
            aria-hidden="true"
          />
          
          {/* Content with z-index to appear above background */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
            <div className="text-center mb-8 md:mb-12">
              <h2 id="faq-heading" className="text-3xl md:text-5xl lg:text-6xl font-medium mb-6 md:mb-8">Frequently Asked Questions</h2>
            </div>

            {/* FAQ Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10 mb-8 md:mb-12" role="list">
              {/* FAQ Card 1 */}
              <article className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100" role="listitem">
                <h3 className="text-lg md:text-xl font-bold text-gray-700 mb-3 md:mb-4 leading-snug">
                  How do I get started?
                </h3>
                <p className="text-sm md:text-base text-black leading-relaxed">
                  GetPawtected is invitation-only. Your <span className="font-bold">property manager</span> will send you an email invitation with a link to set up your account.
                </p>
              </article>

              {/* FAQ Card 2 */}
              <article className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100" role="listitem">
                <h3 className="text-lg md:text-xl font-bold text-gray-700 mb-3 md:mb-4 leading-snug">
                  Can I edit my profile after it is submitted?
                </h3>
                <p className="text-sm md:text-base text-black leading-relaxed">
                  Yes, you can sign in and edit your profile at any time if you have a new pet or need to change your current records.
                </p>
              </article>

              {/* FAQ Card 3 */}
              <article className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100" role="listitem">
                <h3 className="text-lg md:text-xl font-bold text-gray-700 mb-3 md:mb-4 leading-snug">
                  How long does it take to process my profile?
                </h3>
                <p className="text-sm md:text-base text-black leading-relaxed">
                  On average it will take 48-72 hours from the Get Pawtected side. Please note Emotional Support Animals can take up to 10 days for approvals.
                </p>
              </article>

              {/* FAQ Card 4 */}
              <article className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100" role="listitem">
                <h3 className="text-lg md:text-xl font-bold text-gray-700 mb-3 md:mb-4 leading-snug">
                  Will my application expire?
                </h3>
                <p className="text-sm md:text-base text-black leading-relaxed">
                  Yes, this is an annual process and your application will expire annually.
                </p>
              </article>

              {/* FAQ Card 5 */}
              <article className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100" role="listitem">
                <h3 className="text-lg md:text-xl font-bold text-gray-700 mb-3 md:mb-4 leading-snug">
                  Who approves your pet application?
                </h3>
                <p className="text-sm md:text-base text-black leading-relaxed">
                  Your property management company. We provide the facts and they make the decision.
                </p>
              </article>
            </div>

            {/* CTA Button — self-serve sign-up: dev/local only; hard-gated on staging/prod. */}
            {selfServeSignup && (
              <div className="text-center">
                <button
                  className="bg-[color:var(--pmc-accent)] hover:bg-[#2196CC] text-[#FFFFFF] px-8 md:px-12 py-3 md:py-4 rounded-full text-base md:text-lg font-bold uppercase transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2"
                  onClick={() => navigate('/signup')}
                  aria-label="Get started with pet registration"
                >
                  GET STARTED
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="w-full bg-white py-20 md:py-32 lg:py-40" aria-labelledby="cta-heading">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
            <div className="relative text-center mb-2 md:mb-4 px-4">
              <div className="mb-6">
                <h2 id="cta-heading" className="font-poppins mb-4 leading-tight">
                  <span className="text-sky-500 text-7xl font-bold font-['Poppins']">Get</span>
                  <span className="text-purple-900 text-7xl font-bold font-['Poppins']">Pawtected</span>
                </h2>
              </div>
              
              <p className="font-inter text-black max-w-4xl mx-auto text-center text-lg md:text-xl lg:text-2xl leading-7 md:leading-8 mb-6">
                To register your pet and meet your property's requirements, sign in using the invitation your property manager sent you.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 font-poppins justify-center items-center">
                {/* Self-serve sign-up: dev/local only; hard-gated on staging/prod. */}
                {selfServeSignup && (
                  <button
                    className="bg-[color:var(--pmc-accent)] hover:bg-[#2196CC] text-white px-6 md:px-8 py-3 rounded-full font-bold uppercase focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2"
                    onClick={() => navigate('/signup')}
                    aria-label="Create a new profile"
                  >
                    Create a profile
                  </button>
                )}
                <button
                  className="bg-white border-2 border-gray-400 text-gray-600 px-6 md:px-8 py-3 rounded-full font-bold uppercase hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2"
                  onClick={() => navigate('/login')}
                  aria-label="Sign in to your account"
                >
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </section>

        <footer className="relative text-white py-10 md:py-20" style={{ backgroundColor: 'rgba(22, 1, 44, 0.95)' }} role="contentinfo">
          <img 
              src="/landing/foot.jpg"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 lg:px-16 text-center">
            <div className="text-2xl md:text-3xl mb-4" aria-label="GetPawtected">
              <span className="text-white">get</span>
              <span className="text-white">pawtected</span>
            </div>
            <p className="text-[#7b6692] mb-6 md:mb-8">Compliance tool to register pets.</p>
    
            <button 
              className="border-2 border-white text-white px-6 md:px-8 py-3 rounded-full font-bold hover:bg-white hover:text-slate-900 transition-colors mb-6 md:mb-8 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-purple-900" 
              onClick={() => navigate('/login')}
              aria-label="Login to your account"
            >
              LOGIN
            </button>
            <div className="border-t border-gray-700 pt-6 md:pt-8 text-[#7b6692] text-sm md:text-base">
              <p>© 2025 GetPawtected. All Rights Reserved.</p>
              <nav aria-label="Footer navigation" className="mt-2">
                <a href="#" className="hover:text-white cursor-pointer text-[#cab7e8] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-purple-900 rounded">Privacy Policy</a>
                <span className="mx-2 md:mx-4 text-[#cab7e8]" aria-hidden="true"> |</span>
                <a href="#" className="hover:text-white cursor-pointer text-[#cab7e8] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-purple-900 rounded">Terms of Service</a>
              </nav>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
